import type { RealtimeAssistantCompletion } from "./use-realtime-audio"
import type { ActivityMessage, ChatAppResponse } from "@/api/models"

export type VoiceExchange = {
  id: string
  userText: string
  assistantText: string
  isStreaming: boolean
  timestamp: number
}

export type CompletedVoiceTool = {
  name: string
  callId?: string
  success: boolean
  assistantFiles?: string[]
}

export function assistantFilesFromToolCalls(toolCalls: CompletedVoiceTool[]): string[] {
  return Array.from(new Set(toolCalls.flatMap((toolCall) => toolCall.assistantFiles ?? [])))
}

export function mergeCompletedVoiceTools(
  current: CompletedVoiceTool[],
  incoming: CompletedVoiceTool[]
): CompletedVoiceTool[] {
  const merged = [...current]
  for (const toolCall of incoming) {
    const existingIndex = merged.findIndex((candidate) =>
      toolCall.callId
        ? candidate.callId === toolCall.callId
        : !candidate.callId &&
          candidate.name === toolCall.name &&
          candidate.success === toolCall.success
    )
    if (existingIndex < 0) {
      merged.push(toolCall)
      continue
    }
    const existing = merged[existingIndex]
    merged[existingIndex] = {
      ...existing,
      ...toolCall,
      assistantFiles: Array.from(
        new Set([...(existing.assistantFiles ?? []), ...(toolCall.assistantFiles ?? [])])
      ),
    }
  }
  return merged
}

export function mergeVoiceActivities(
  current: ActivityMessage[],
  completion: RealtimeAssistantCompletion
): ActivityMessage[] {
  const merged = [...current]
  for (const incoming of completion.activities) {
    const success = incoming.details?.success
    const alreadyTracked = merged.some(
      (activity) =>
        activity.activity_type === incoming.activityType &&
        activity.message === incoming.message &&
        activity.details?.success === success
    )
    if (alreadyTracked) continue
    merged.push({
      type: "activity",
      activity_type: incoming.activityType,
      message: incoming.message,
      details: incoming.details,
      timestamp: incoming.timestamp ?? Date.now(),
    })
  }
  return merged
}

const toolDisplayInfo: Record<string, { activityType: string; messageKey: string }> = {
  web_search: { activityType: "web_search", messageKey: "web_search_started" },
  web_scrape: { activityType: "web_scrape", messageKey: "web_scrape_started" },
  library_search: { activityType: "library_search", messageKey: "library_search_started" },
  file_search: { activityType: "file_search", messageKey: "file_search_started" },
  microsoft_365_search: {
    activityType: "microsoft_365_search",
    messageKey: "microsoft_365_search_started",
  },
  training_content: { activityType: "library_search", messageKey: "library_search_started" },
}

export function createToolActivity(toolName: string): ActivityMessage {
  const info = toolDisplayInfo[toolName] ?? { activityType: toolName, messageKey: toolName }
  return {
    type: "activity",
    activity_type: info.activityType,
    message: `Using ${toolName.replace(/_/g, " ")}...`,
    details: {
      translation_key: info.messageKey,
    },
    timestamp: Date.now(),
  }
}

type StreamingVoiceResponseOptions = {
  text: string
  activities?: ActivityMessage[]
  assistantFiles?: string[]
  sessionId?: string
}

export function createStreamingVoiceResponse({
  text,
  activities = [],
  assistantFiles = [],
  sessionId,
}: StreamingVoiceResponseOptions): ChatAppResponse {
  return {
    content: text,
    context: {
      data_points: [],
      thoughts: [],
      followup_questions: [],
      ...(sessionId ? { session_id: sessionId } : {}),
    },
    session_state: null,
    completed: false,
    activities,
    ...(assistantFiles.length ? { assistant_files: assistantFiles } : {}),
  }
}
